<template>
  <div class="fullscreen-page">
    <HeaderMenu title="System Information" />
    <main class="page-content">
      <div class="system-card">
        <h2>Backend Health Status</h2>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <p>Checking system status...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-card">
            <h3>❌ System Check Failed</h3>
            <p>{{ error }}</p>
            <button @click="loadHealthStatus" class="primary btn">
              Retry
            </button>
          </div>
        </div>

        <!-- Success State -->
        <div v-else-if="healthData" class="health-info">
          <div class="status-grid">
			<div class="status-item">
				<span class="status-label">Frontend Version:</span>
				<span class="status-value">{{ versionInfo?.frontend_version || 'Unknown' }}</span>
			</div>
			<div class="status-item">
				<span class="status-label">Backend Version:</span>
				<span class="status-value">{{ healthData?.backend_version || 'Unknown' }}</span>
			</div>
            <div class="status-item">
              <span class="status-label">Status:</span>
              <span class="status-value" :class="{ healthy: healthData.status === 'ok', unhealthy: healthData.status !== 'ok' }">
                {{ healthData.status === 'ok' ? '✅ Healthy' : '❌ Unhealthy' }}
              </span>
            </div>
            
            <div class="status-item">
              <span class="status-label">Timestamp:</span>
              <span class="status-value">{{ formatTimestamp(healthData.timestamp) }}</span>
            </div>
            
            <div class="status-item">
              <span class="status-label">Database:</span>
              <span class="status-value" :class="{ healthy: healthData.status === 'ok', unhealthy: healthData.status !== 'ok' }">
                {{ healthData.status === 'ok' ? '✅ Connected' : '❌ Connection Failed' }}
              </span>
            </div>
          </div>

          <div class="actions">
            <button @click="refreshSystemStatus" class="primary btn" :disabled="loading">
              {{ loading ? 'Refreshing...' : 'Refresh Status' }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useVersionInfo } from '../composables/useVersionInfo'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { API } from '../api/client'
import HeaderMenu from '../components/HeaderMenu.vue'

interface HealthData {
  status: string
  timestamp: string
  backend_version?: string
  [key: string]: any
}


const router = useRouter()
const { user: currentUser } = useAuth()
const isAdmin = computed(() => currentUser.value?.is_admin ?? false)

const healthData = ref<HealthData | null>(null)
const { versionInfo, loadVersionInfo } = useVersionInfo()
const loading = ref(false)
const error = ref('')


onMounted(async () => {
  // Check if user is admin
  if (!isAdmin.value) {
    router.push('/')
    return
  }
  await Promise.all([loadVersionInfo(), loadHealthStatus()])
})

async function loadHealthStatus() {
  loading.value = true
  error.value = ''

  try {
    const data = await API.getHealth()
    healthData.value = data
  } catch (err: any) {
    console.error('Failed to load health status:', err)
    error.value = err.response?.data?.message || 'Unable to connect to backend'
    healthData.value = null
  } finally {
    loading.value = false
  }
}


async function refreshSystemStatus() {
  await Promise.all([loadVersionInfo(), loadHealthStatus()])
}

function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp)
    return date.toLocaleString()
  } catch {
    return timestamp
  }
}
</script>

<style scoped>
.page-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.system-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.system-card h2 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #333;
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error-state {
  text-align: center;
}

.error-card {
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.error-card h3 {
  margin-top: 0;
  color: #dc2626;
}

.error-card p {
  color: #7f1d1d;
  margin-bottom: 1rem;
}

.health-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.status-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.status-label {
  font-weight: 600;
  color: #334155;
}

.status-value {
  font-weight: 600;
  font-family: monospace;
}

.status-value.healthy {
  color: #16a34a;
}

.status-value.unhealthy {
  color: #dc2626;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

@media (max-width: 768px) {
  .status-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .status-value {
    align-self: flex-end;
  }
}
</style>